import { useMemo, useState } from 'react';
import { groupBy } from '../../../shared/utils/arrayUtils.ts';
import { partDisplayName } from '../../../shared/utils/partDisplayName.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { ProgramNameMapping } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { ClearFiltersButton } from '../clearFiltersButton.tsx';
import { LinkButton } from '../linkButton.tsx';
import { AddProgramDialog } from './addProgramDialog.tsx';


interface AddMappingDialogState {
    partNumber: string;
    protocolId: string;
    mappings: ProgramNameMapping[];
}


export function ProgramNameMappings() {
    const [filterInput, setFilterInput] = useState('');
    const [addMappingDialogState, setAddMappingDialogState] = useState<AddMappingDialogState | null>(null);

    const { data: mappingsByProgram, error, isLoading, refetch } = trpc.products.getNameMappings.useQuery(
        { filterVisible: true },
    );

    const mappingsByPartName = useMemo(() => {
        if (!mappingsByProgram) return null;
        const withDisplayName = mappingsByProgram.map(mapping => ({
            ...mapping,
            partDisplayName: partDisplayName(mapping.partName, mapping.partRevision)
        }));
        const grouped = groupBy(withDisplayName, it => it.partDisplayName);
        const sorted = sortBy(grouped, it => it[0].partDisplayName);
        return sorted;
    }, [mappingsByProgram]);

    const filteredMappings = mappingsByPartName?.filter(it =>
        it[0].partDisplayName.toLowerCase().includes(filterInput.toLowerCase()) ||
        it.some(mapping => mapping.program.toLowerCase().includes(filterInput.toLowerCase()))
    );

    const handleClearFilters = () => {
        setFilterInput('');
    }


    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>An error has occurred: {error.message}</div>;
    return <>
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: '600px' }}>

                <input
                    style={{ marginLeft: '18px' }}
                    type='text'
                    placeholder='Search'
                    value={filterInput}
                    onChange={e => setFilterInput(e.target.value)}
                />
                <ClearFiltersButton onClick={() => handleClearFilters()} />
            </div>

            <table className='program-name-mapping--table'>
                <thead>
                    <tr>
                        <th>Part number</th>
                        <th>Program Name</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {filteredMappings?.map(it => (
                        <tr key={it[0].partDisplayName}>
                            <td>{it[0].partDisplayName}</td>
                            <td>
                                {
                                    it.map(mapping => mapping.program).map((program, index) => (
                                        <div key={index}>{program}</div>
                                    ))
                                }
                            </td>
                            <td>
                                <LinkButton
                                    onClick={() => setAddMappingDialogState({
                                        partNumber: it[0].partName,
                                        protocolId: it[0].protocolId!,
                                        mappings: it,
                                    })}
                                    disabled={it[0].protocolId == null}
                                >
                                    Add
                                </LinkButton>
                            </td>
                        </tr>
                    ))}
                </tbody>

            </table>
        </div>

        {addMappingDialogState &&
            <AddProgramDialog
                partNumber={addMappingDialogState.partNumber}
                protocolId={addMappingDialogState.protocolId}
                mappings={addMappingDialogState.mappings}
                onSuccess={() => refetch()}
                onClose={() => setAddMappingDialogState(null)}
            />
        }
    </>
}
